// Add the Firebase products that you want to use
import "firebase/database";
import "firebase/auth";
import "firebase/storage";
import "firebase/functions";

import {
  get as _get
} from "lodash";
// Firebase App (the core Firebase SDK) is always required and must be listed first
import firebase from "firebase/app";
import { claimedCounterValue, nextCounterValue } from "./counter.mjs";

// Application Config
const appConfig = JSON.parse(import.meta.env.VITE_APP_CONFIG);

if (firebase.apps.length === 0) {
  firebase.initializeApp(appConfig.firebaseConfig);
}

const $db = firebase.database();
const $storage = firebase.storage();
const auth = firebase.auth();

// Returns a reference to a deployed callable Cloud Function.
// Returns a reference to a deployed callable Cloud Function.
// Matches the pattern used in the (now deprecated) flight-test-crew standalone app.
const callable = (name) => firebase.app().functions().httpsCallable(name);

// list of currently active firebase watchers
const firebaseWatchers = new Set();

// --- Elasticsearch read-your-writes tracking ---
// The tenant list/attachable views can source rows from Elasticsearch, which is
// updated asynchronously by a Cloud Function after the RTDB write. Until the
// index catches up, a re-fetch would return the stale pre-edit document. When a
// form writes an ES-indexed record it stamps `_esSync` (a ms timestamp) into the
// payload; that value flows through the indexer into the ES `_source`. We record
// the token we wrote per (type, id) below so `confirmIndexed` can poll until the
// index reflects it, and `isEsHitStale` lets list loaders skip a hit that is
// still behind (falling back to a live RTDB read) rather than showing stale data.
const ES_SYNC_FIELD = "_esSync";
const ES_SYNC_TIMEOUT_MS = 8000;
const esSyncPending = new Map();
const esSyncKey = (type, id) => `${type}:${id}`;

const createLog = (userUid, type, location) => {
  const logRef = $db.ref("Logs").push({
    User: userUid,
    Type: type,
    Location: location,
    Date: new Date().toISOString()
  });
  logRef.child("ID").set(logRef.key);
  return logRef;
};

const db = {
  async once(location) {
    const ref = await $db.ref(location).once("value");
    return ref.val();
  },
  async keys(location, keys) {
    const data = await Promise.all(keys.map(key => $db.ref(location + key).once("value")))
    return data.map(d => d.val());
  },
  async query(location, key, value, store, storeKey) {
    const ref = await $db.ref(location).orderByChild(key).equalTo(value).once("value");
    store.commit("save", {
      [storeKey || location]: ref.val()
    });
    return ref.val();
  },
  async between(location, key, value1, value2) {
    const ref = await $db.ref(location).orderByChild(key).startAt(value1).endAt(value2).once("value");
    return ref.val();
  },
  async store(location, store, storeKey) {
    const ref = await $db.ref(location).once("value");
    store.commit("save", {
      [storeKey || location]: ref.val()
    });
    return ref.val();
  },
  /**
   * Paginated/sorted/filtered search over the tenant's Elasticsearch index for
   * a whitelisted Database item type (powers ItemListView server-side tables).
   *
   * @returns {Promise<{ total: number, from: number, size: number, hits: object[] }>}
   */
  async searchDatabase({ type, query, from, size, sort, filters, cancelled, hidden } = {}) {
    const search = callable("searchDatabase");
    const res = await search({
      databaseURL: appConfig?.firebaseConfig?.databaseURL,
      type,
      query,
      from,
      size,
      sort,
      filters,
      cancelled,
      hidden,
    });
    return res.data;
  },
  /**
   * Run one registered database metrics report. The callable returns aggregation
   * rows only (`{ rows, seriesKeys, years }`), not the underlying documents.
   */
  async databaseReport({ report, params } = {}) {
    const run = callable("databaseReport");
    const res = await run({
      databaseURL: appConfig?.firebaseConfig?.databaseURL,
      report,
      params,
    });
    return res.data;
  },
  /**
   * Resolve detected document, drawing, design-change, or job-card numbers to
   * database ids via the tenant master items index.
   *
   * @returns {Promise<{ matches: object[] }>}
   */
  async resolveDatabaseItems({ numbers } = {}) {
    const resolve = callable("resolveDatabaseItems");
    const res = await resolve({
      databaseURL: appConfig?.firebaseConfig?.databaseURL,
      numbers,
    });
    return res.data;
  },
  /**
   * Fetch-all children of a parent from the tenant's Elasticsearch index
   * (powers ItemView attachable tables). Bounded server-side.
   *
   * @returns {Promise<{ total: number, from: number, size: number, hits: object[] }>}
   */
  async findAttachedItems({ parentId, parentType, type, query, size } = {}) {
    const search = callable("searchDatabase");
    const res = await search({
      databaseURL: appConfig?.firebaseConfig?.databaseURL,
      type,
      parentId,
      parentType,
      query,
      size,
    });
    return res.data;
  },
  /**
   * Fetch a specific set of documents by ID from the tenant's Elasticsearch
   * index (powers attachable tables, whose related id-set comes from the
   * parent's relationship maps). Ids are chunked so large relationship sets
   * stay within the per-request bound; results are merged.
   *
   * @returns {Promise<{ hits: object[] }>}
   */
  async findItemsByIds({ type, ids, size } = {}) {
    const cleanIds = (Array.isArray(ids) ? ids : [])
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map((value) => String(value));
    if (!type || cleanIds.length === 0) {
      return { hits: [] };
    }

    const CHUNK_SIZE = 500;
    const chunks = [];
    for (let i = 0; i < cleanIds.length; i += CHUNK_SIZE) {
      chunks.push(cleanIds.slice(i, i + CHUNK_SIZE));
    }

    const search = callable("searchDatabase");
    const responses = await Promise.all(
      chunks.map((chunk) =>
        search({
          databaseURL: appConfig?.firebaseConfig?.databaseURL,
          type,
          ids: chunk,
          size: size || chunk.length,
        })
      )
    );

    const hits = responses.flatMap((res) => res?.data?.hits || []);
    return { hits };
  },
  /** The payload field a form stamps so a write can be tracked into ES. */
  esSyncField() {
    return ES_SYNC_FIELD;
  },
  /** Record that we are waiting for ES to reflect a write of (type, id). */
  markEsSyncPending(type, id, token) {
    if (!type || id === undefined || id === null || !token) return;
    esSyncPending.set(esSyncKey(type, id), token);
  },
  /** Stop tracking a pending write for (type, id). */
  clearEsSyncPending(type, id) {
    if (!type || id === undefined || id === null) return;
    esSyncPending.delete(esSyncKey(type, id));
  },
  /**
   * True when an ES hit is still behind a write we are tracking, i.e. the index
   * has not yet caught up. Self-clears the pending marker once the hit is fresh.
   */
  isEsHitStale(type, hit) {
    if (!hit || hit.ID === undefined || hit.ID === null) return false;
    const key = esSyncKey(type, hit.ID);
    const token = esSyncPending.get(key);
    if (token === undefined) return false;
    const indexed = Number(hit[ES_SYNC_FIELD]) || 0;
    if (indexed >= token) {
      esSyncPending.delete(key);
      return false;
    }
    return true;
  },
  /**
   * Poll the tenant's ES index until the write identified by `token` is
   * reflected for (type, id), or until `timeoutMs` elapses. On success the
   * pending marker is cleared; on timeout it is left in place so list loaders
   * keep treating a stale hit as stale until a later fresh read clears it.
   *
   * @returns {Promise<{ confirmed: boolean, hit?: object }>}
   */
  async confirmIndexed({ type, id, token, timeoutMs = ES_SYNC_TIMEOUT_MS } = {}) {
    if (!type || id === undefined || id === null || !token) {
      return { confirmed: false };
    }
    const key = esSyncKey(type, id);
    esSyncPending.set(key, token);
    const deadline = Date.now() + timeoutMs;
    const delays = [250, 400, 600, 900, 1200, 1500];
    let attempt = 0;
    while (Date.now() < deadline) {
      try {
        const { hits } = await this.findItemsByIds({ type, ids: [id] });
        const hit = (hits || []).find((h) => h && String(h.ID) === String(id));
        if (hit && (Number(hit[ES_SYNC_FIELD]) || 0) >= token) {
          esSyncPending.delete(key);
          return { confirmed: true, hit };
        }
      } catch (err) {
        // Transient (e.g. cold start) — keep polling until the deadline.
        console.error("confirmIndexed poll failed:", err?.message || err);
      }
      const remaining = deadline - Date.now();
      if (remaining <= 0) break;
      const wait = Math.min(delays[Math.min(attempt, delays.length - 1)], remaining);
      attempt++;
      await new Promise((resolve) => setTimeout(resolve, wait));
    }
    return { confirmed: false };
  },
  async update(location, data) {
    const curentUser = auth.currentUser;
    createLog(curentUser ? curentUser.uid : "Unknown User", "Update", location);
    return await $db.ref(location).update(data);
  },
  async set(location, data) {
    const curentUser = auth.currentUser;
    createLog(curentUser ? curentUser.uid : "Unknown User", "Set", location);
    return await $db.ref(location).set(data);
  },
  async delete(location) {
    const curentUser = auth.currentUser;
    createLog(curentUser ? curentUser.uid : "Unknown User", "Delete", location);

    return await $db.ref(location).remove();
  },
  async push(location, data) {
    const curentUser = auth.currentUser;
    createLog(curentUser ? curentUser.uid : "Unknown User", "Create", location);
    let ref = await $db.ref(location).push(data);
    ref.child("ID").set(ref.key);
    return ref;
  },
  async watch(location, store, storeKey) {
    return await new Promise((resolve, reject) => {
      try {
        
        if (firebaseWatchers.has(storeKey || location)) {
          
          resolve(store.state[storeKey || location]);
          return;
        }
        else {
          firebaseWatchers.add(storeKey || location)
          $db.ref(location).on("value", ref => {
            store.commit("save", {
              [storeKey || location]: ref.val()
            });
            resolve(store.state[storeKey || location]);
          }, (err) => {
            reject(err);
          });
        }
      } catch (err) {
        console.error(err);
      }
    });
  },
  async download(location) {
    return await $storage.ref(location).getDownloadURL()
  },
  async upload(location, file, metadata) {
    const curentUser = auth.currentUser;
    createLog(curentUser ? curentUser.uid : "Unknown User", "Upload", location);
    return await $storage.ref(location).child(file.name || metadata.name).put(file, { "customeMetadata": metadata });
  },
  async remove(location, file) {
    return await $storage.ref(location).child(file).delete();
  },
  unwatch(location, storeKey) {
    $db.ref(location).off("value");
    firebaseWatchers.delete(typeof storeKey === "string" ? storeKey : location);
  },
  ref(location) {
    return $db.ref(location);
  },
  utils: {
    async addAllTo(data, location, key, target) {
      for (const k of Object.keys(data[key])) {
        await $db.ref(`${location}/${k}/${target}/${data.ID}`).set(true);
      }
    },
    /**
     * Atomically claim the current counter and store current + 1.
     * Concurrent callers (including parallel E2E shards) cannot take the
     * same number or drop an increment: Firebase retries the transaction
     * with the latest server value. Returns the pre-increment integer,
     * which is the number that should be stamped on the new record.
     */
    async allocateCount(location) {
      const result = await $db.ref(location).transaction((current) => nextCounterValue(current));
      if (!result.committed) {
        throw new Error(`Counter allocation was not committed at ${location}`);
      }
      return claimedCounterValue(result.snapshot.val());
    },
    async upCount(location) {
      await db.utils.allocateCount(location);
    },
    async updateRefs(data, location, keys) {
      for (const key of keys) {

        await $db.ref(`${location}/${data.ID}/${key}`).set(_get(data, key, ""));
      }
    }
  }
}


export {
  db,
  auth,
  callable,
  appConfig
};
